import { ProjectSchema, type Project } from "@tasduvar/engine";
import type { ProjectSummaryDto } from "@tasduvar/shared";
import { api, ApiClientError, NetworkError } from "../api/client";
import type { ProjectStore, ProjectSummary } from "./types";

/** PUT 409: sunucudaki sürüm bizim bildiğimizden yeni. */
export class ConflictError extends Error {
  constructor(public readonly server: { updatedAt: string; digest: string }) { super("Proje sunucuda değişmiş"); this.name = "ConflictError"; }
}

const CACHE = "tasduvar:cloud:";
const QUEUE = "tasduvar:queue";
const BASE = "tasduvar:cloudbase";

function ls(): Storage | null { try { return window.localStorage; } catch { return null; } }
const readJson = <T,>(key: string): T | null => { try { const raw = ls()?.getItem(key); return raw ? (JSON.parse(raw) as T) : null; } catch { return null; } };
const writeJson = (key: string, v: unknown): void => { try { ls()?.setItem(key, JSON.stringify(v)); } catch { /* dolu/yasak */ } };

/**
 * Bulut proje deposu. Sunucu `updatedAt`'ini hatırlar ve her PUT'ta `baseUpdatedAt` gönderir (409 → ConflictError).
 * Ağ yokken kaydı localStorage'a yazar ve kuyruğa alır; `flush()` (online olayı) kuyruğu boşaltır.
 */
export class ApiProjectStore implements ProjectStore {
  private base: Record<string, string> = readJson<Record<string, string>>(BASE) ?? {};
  private setBase(id: string, updatedAt: string | null): void {
    if (updatedAt) this.base[id] = updatedAt; else delete this.base[id];
    writeJson(BASE, this.base);
  }
  baseOf(id: string): string | undefined { return this.base[id]; }

  async list(): Promise<ProjectSummary[]> {
    const rows = await api<ProjectSummaryDto[]>("GET", "/projects");
    return rows.map((r) => ({ id: r.id, name: r.name, updatedAt: r.updatedAt, wallCount: r.wallCount }));
  }
  async load(id: string): Promise<Project | null> {
    try {
      const r = await api<{ project: unknown; updatedAt: string }>("GET", `/projects/${encodeURIComponent(id)}`);
      const p = ProjectSchema.parse(r.project);
      this.setBase(id, r.updatedAt);
      writeJson(CACHE + id, p);
      return p;
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 404) return null;
      if (e instanceof NetworkError) { const c = readJson<unknown>(CACHE + id); return c ? ProjectSchema.parse(c) : null; } // çevrimdışı kopya
      throw e;
    }
  }
  /** `force` ile sunucudaki sürümün üzerine yazar (çakışma diyaloğu "Üzerine yaz"). */
  async save(p: Project, opts: { force?: boolean; note?: string } = {}): Promise<{ queued: boolean }> {
    const project = { ...p, updatedAt: new Date().toISOString() };
    writeJson(CACHE + p.id, project);
    try {
      const r = await api<{ updatedAt: string }>("PUT", `/projects/${encodeURIComponent(p.id)}`, {
        project, baseUpdatedAt: opts.force ? undefined : this.base[p.id], note: opts.note,
      });
      this.setBase(p.id, r.updatedAt);
      this.dequeue(p.id);
      return { queued: false };
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 409) {
        const server = (e.body as { server: { updatedAt: string; digest: string } }).server;
        throw new ConflictError(server);
      }
      if (e instanceof NetworkError) { this.enqueue(p.id); return { queued: true }; } // çevrimdışı: kuyrukta bekler
      throw e;
    }
  }
  /** Silinenler (çöp kutusu): silinme tarihiyle */
  async listDeleted(): Promise<(ProjectSummary & { deletedAt: string })[]> {
    const rows = await api<ProjectSummaryDto[]>("GET", "/projects?silinmis=1");
    return rows.map((r) => ({ id: r.id, name: r.name, updatedAt: r.updatedAt, wallCount: r.wallCount, deletedAt: r.deletedAt ?? r.updatedAt }));
  }
  async restore(id: string): Promise<void> { await api("POST", `/projects/${encodeURIComponent(id)}/restore`, {}); }
  async purge(id: string): Promise<void> { await api("DELETE", `/projects/${encodeURIComponent(id)}/purge`); }
  async remove(id: string): Promise<void> {
    await api("DELETE", `/projects/${encodeURIComponent(id)}`);
    this.setBase(id, null); ls()?.removeItem(CACHE + id); this.dequeue(id);
  }
  /** Sunucudaki güncel sürümü alır ve base'i ona eşitler (çakışma diyaloğu "Sunucuyu al"). */
  async pull(id: string): Promise<Project | null> { return this.load(id); }

  // ── Çevrimdışı kuyruk ─────────────────────────────────────────────────────
  queue(): string[] { return readJson<string[]>(QUEUE) ?? []; }
  private enqueue(id: string): void { const q = this.queue(); if (!q.includes(id)) writeJson(QUEUE, [...q, id]); }
  private dequeue(id: string): void { const q = this.queue(); if (q.includes(id)) writeJson(QUEUE, q.filter((x) => x !== id)); }
  /** Kuyruktaki projeleri sırayla gönderir; başarısız olanlar (çakışma dâhil) kuyrukta kalır ve döner. */
  async flush(): Promise<{ sent: string[]; failed: { id: string; error: Error }[] }> {
    const sent: string[] = []; const failed: { id: string; error: Error }[] = [];
    for (const id of this.queue()) {
      const p = readJson<unknown>(CACHE + id);
      if (!p) { this.dequeue(id); continue; }
      try { await this.save(ProjectSchema.parse(p)); if (!this.queue().includes(id)) sent.push(id); }
      catch (e) { failed.push({ id, error: e instanceof Error ? e : new Error(String(e)) }); }
    }
    return { sent, failed };
  }
}
